import { Tags } from 'application/Shared/Components/Tags/Tags';
import {
    useDeleteGlobalTag,
    useFetchGlobalTags,
    usePostGlobalTag,
    usePutGlobalTag
} from 'hooks/dataAccess/GlobalTag';
import React, { useMemo } from 'react';

/**
 * A custom React hook that fetches global tags from the server.
 * This hook acts as an adapter, abstracting away the underlying implementation details
 * of fetching the tags from the server.
 *
 * @returns {Object} An object containing the following properties:
 *   - data: An array of tag objects, where each object has properties like color,
 *           description, and other tag details.
 *   - loading: A boolean indicating whether the tags are currently being fetched.
 *   - refetchData: A function that can be called to refetch the tags from the server.
 */
export const useGetTags = () => {
    const globalTagsParams = useMemo(() => ({ size: -1 }), []);

    const {
        globalTags = [],
        loading,
        refetchData
    } = useFetchGlobalTags(globalTagsParams);

    const data = globalTags.map((tag) => ({
        color: tag.defaultColor || 'tag.option1',
        description: tag.defaultDescription,
        managed: tag.auvikManaged,
        ...tag
    }));

    return { data, loading, refetchData };
};

/**
 * A custom React hook that allows creating a new global tag on the server.
 * This hook acts as an adapter, abstracting away the underlying implementation details
 * of creating the tag on the server.
 *
 * @returns {Object} An object containing the following properties:
 *   - postTag: A function that takes a payload object with properties like name, color,
 *              and description, and creates a new tag on the server.
 *   - saving: A boolean indicating whether a new tag is currently being created.
 */
export const usePostTags = () => {
    const { postGlobalTag, saving } = usePostGlobalTag();

    const postTag = async (payload) => {
        await postGlobalTag({
            auvikManaged: true,
            defaultColor: payload.color,
            defaultDescription: payload.description,
            name: payload.name
        });
    };

    return { postTag, saving };
};

/**
 * A custom React hook that allows updating an existing global tag on the server.
 * This hook acts as an adapter, abstracting away the underlying implementation details
 * of updating the tag on the server.
 *
 * @returns {Object} An object containing the following properties:
 *   - putTag: A function that takes a payload object with properties like id, name, color,
 *             and description, and updates the corresponding tag on the server.
 *   - saving: A boolean indicating whether a tag is currently being updated.
 */
export const usePutTags = () => {
    const { putGlobalTag, saving } = usePutGlobalTag();

    const putTag = async (payload) => {
        await putGlobalTag({
            defaultColor: payload.color,
            defaultDescription: payload.description,
            id: payload.id,
            name: payload.name
        });
    };

    return { putTag, saving };
};

/**
 * A custom React hook that allows deleting an existing global tag from the server.
 * This hook acts as an adapter, abstracting away the underlying implementation details
 * of deleting the tag from the server.
 *
 * @returns {Object} An object containing the following properties:
 *   - deleteTag: A function that takes a payload object with the id property,
 *                and deletes the corresponding tag from the server.
 *   - deleting: A boolean indicating whether a tag is currently being deleted.
 */
export const useDeleteTags = () => {
    const { deleteGlobalTag, deleting } = useDeleteGlobalTag();

    const deleteTag = async (payload) => {
        await deleteGlobalTag({ id: payload.id });
    };

    return { deleteTag, deleting };
};

/**
 * Global Admin wrapper for the Tags component.
 *
 * @returns {JSX.Element} The GlobalTags component.
 */
export const GlobalTags = () => (
    <Tags hooks={{ useDeleteTags, useGetTags, usePostTags, usePutTags }} />
);
